/**
 * OpenReplay Network Sanitizer
 *
 * This module provides sanitization functions for OpenReplay session recordings.
 * It ensures sensitive patient data (PII) is not exposed in network requests/responses
 * captured by OpenReplay's session replay feature.
 *
 * What it sanitizes:
 * - Sensitive fields: patient, pin (recursively in any request/response)
 * - URL parameters: documentNumber
 * - Sensitive URLs: Complete request/response bodies for specific endpoints
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Configuration for sanitization rules
 */
const SANITIZATION_CONFIG = {
  // Fields that are always sanitized regardless of endpoint
  sensitiveFields: ["patient", "pin", "documentNumber"],

  // URLs where the entire request body is sanitized
  sensitiveRequestUrls: [],

  // URLs where the entire response body is sanitized
  sensitiveResponseUrls: ["/prod/patients"],

  // Query parameters that should always be masked
  sensitiveQueryParams: ["documentNumber"],
};

// ============================================================================
// SANITIZATION FUNCTIONS
// ============================================================================

const sanitizeAllValues = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return "****";
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeAllValues(item));
  }

  if (typeof obj === "object") {
    const sanitized: any = {};
    for (const key in obj) {
      sanitized[key] = sanitizeAllValues(obj[key]);
    }
    return sanitized;
  }

  return "****";
};

const sanitizeSensitiveFields = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeSensitiveFields(item));
  }

  if (typeof obj === "object") {
    const result: any = {};
    for (const key in obj) {
      if (SANITIZATION_CONFIG.sensitiveFields.includes(key)) {
        // Sanitize entire value of sensitive field
        result[key] = sanitizeAllValues(obj[key]);
      } else {
        // Not sensitive: continue searching recursively
        result[key] = sanitizeSensitiveFields(obj[key]);
      }
    }
    return result;
  }

  return obj;
};

const parseBody = (body: any) => {
  if (typeof body === "string") {
    try {
      return JSON.parse(body);
    } catch (e) {
      return null;
    }
  }
  return body;
};

const serializeBody = (body: any, originalBody: any) => {
  if (typeof originalBody === "string") {
    return JSON.stringify(body);
  }
  return body;
};

/**
 * Checks if a URL matches any of the sensitive URLs in the provided list
 */
const isUrlSensitive = (url: string, urlList: readonly string[]): boolean => {
  if (!url) return false;

  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();

    return urlList.some((sensitiveUrl) => {
      const sensitive = sensitiveUrl.toLowerCase();
      return pathname === sensitive || pathname === sensitive + "/";
    });
  } catch (e) {
    // Fallback for relative URLs
    return urlList.some((sensitiveUrl) => {
      const sensitive = sensitiveUrl.toLowerCase();
      const urlLower = url.toLowerCase();
      // Match exact or with trailing slash/query params
      return (
        urlLower === sensitive ||
        urlLower === sensitive + "/" ||
        urlLower.startsWith(sensitive + "?") ||
        urlLower.startsWith(sensitive + "/?")
      );
    });
  }
};

/**
 * Sanitizes sensitive query parameters in URLs
 */
const sanitizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;

    SANITIZATION_CONFIG.sensitiveQueryParams.forEach((param) => {
      if (params.has(param)) {
        params.set(param, "****");
      }
    });

    return urlObj.toString();
  } catch (e) {
    return url;
  }
};

/**
 * Generic payload sanitizer that handles the parse-check-sanitize-serialize flow
 */
const sanitizePayload = (
  body: any,
  requestUrl: string,
  sensitiveUrls: readonly string[]
): any => {
  if (!body) return body;

  const originalBody = body;
  const parsedBody = parseBody(originalBody);

  if (!parsedBody) return body;

  const sanitizedBody = isUrlSensitive(requestUrl, sensitiveUrls)
    ? sanitizeAllValues(parsedBody)
    : sanitizeSensitiveFields(parsedBody);

  return serializeBody(sanitizedBody, originalBody);
};

/**
 * Main sanitizer function used by OpenReplay network capture
 * Sanitizes URLs, requests, and responses to prevent PII exposure
 */
export const sanitizeData = (data: any) => {
  const requestUrl = data.url || data.request?.url;

  // Sanitize URL
  if (data.url) {
    data.url = sanitizeUrl(data.url);
  } else if (data.request?.url) {
    data.request.url = sanitizeUrl(data.request.url);
  }

  // Sanitize request body
  if (data.request?.body) {
    data.request.body = sanitizePayload(
      data.request.body,
      requestUrl,
      SANITIZATION_CONFIG.sensitiveRequestUrls
    );
  }

  // Sanitize response body
  if (data.response?.body) {
    data.response.body = sanitizePayload(
      data.response.body,
      requestUrl,
      SANITIZATION_CONFIG.sensitiveResponseUrls
    );
  }

  return data;
};
